import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg, formatDate, formatTime, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AdminHead, FilterSelect, Table, Td } from "@/components/admin/AdminUI";
import { StatusBadge } from "@/components/StatusBadge";
import { PageLoader } from "@/components/layout/RequireAuth";

const STATUSES = [["", "Semua status"], ["menunggu_pembayaran", "Menunggu Pembayaran"], ["dibayar", "Dibayar"], ["dikerjakan", "Dikerjakan"],
  ["menunggu_konfirmasi", "Menunggu Konfirmasi"], ["selesai", "Selesai"], ["dibatalkan", "Dibatalkan"], ["dikomplain", "Dikomplain"]];

export function AdminOrders() {
  const [f, setF] = useState({ status: "", source: "", date_from: "", date_to: "" });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/orders", { params }).then((r) => setRows(r.data));
  }, [f]);
  return (
    <div data-testid="admin-orders">
      <AdminHead title="Pesanan & Komplain" />
      <div className="mb-4 flex flex-wrap gap-2">
        <FilterSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testId="orders-status-filter" options={STATUSES} />
        <FilterSelect value={f.source} onChange={(v) => setF({ ...f, source: v })} testId="orders-source-filter" options={[["", "Semua jenis"], ["quote", "Dari penawaran"], ["package", "Paket"]]} />
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-40 rounded-xl" data-testid="orders-date-from" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-40 rounded-xl" data-testid="orders-date-to" />
      </div>
      {rows === null ? <PageLoader /> : (
        <Table cols={["Pesanan", "Pelanggan", "Penyedia", "Jenis", "Status", "Harga", "Dibuat"]} testId="orders-table" rows={rows.map((o) => (
          <tr key={o.order_id} data-testid={`admin-order-row-${o.order_id}`}>
            <Td><Link to={`/admin/pesanan/${o.order_id}`} className="font-semibold text-teal-700">{o.title}</Link></Td>
            <Td>{o.customer_name}</Td><Td>{o.provider_name}</Td><Td>{o.source === "package" ? "Paket" : "Penawaran"}</Td>
            <Td><StatusBadge status={o.status} />{o.refunded && <span className="ml-1 text-xs text-rose-700">(Refund)</span>}</Td>
            <Td>{rupiah(o.price)}</Td><Td>{formatDate(o.created_at)}</Td>
          </tr>
        ))} />
      )}
    </div>
  );
}

export function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/admin/orders/${id}`).then((r) => setO(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (action) => {
    try { await api.post(`/admin/orders/${id}/resolve`, { action, note }); toast.success("Komplain diselesaikan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!o) return <PageLoader />;
  const t = o.transaction;

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/pesanan" className="mb-3 inline-flex items-center gap-1 text-sm text-stone-500"><ArrowLeft className="h-4 w-4" /> Pesanan</Link>
      <AdminHead title={o.title} desc={`${o.customer_name} → ${o.provider_name} · ${o.city_name} · ${rupiah(o.price)}`}>
        <StatusBadge status={o.status} testId="admin-order-status" />{o.refunded && <span className="text-xs font-semibold text-rose-700">Refund (simulasi)</span>}
      </AdminHead>
      {o.status === "dikomplain" && (
        <div className="card-soft mb-6 space-y-3 border-rose-200 p-5" data-testid="resolve-panel">
          <p className="font-bold text-stone-900">Selesaikan komplain</p>
          <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-800">Laporan pelanggan: {o.complaint_reason}</p>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan admin (wajib)" className="rounded-xl" data-testid="resolve-note-input" />
          <div className="flex flex-wrap gap-2">
            <Button disabled={note.trim().length < 5} onClick={() => resolve("lanjutkan")} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="resolve-continue-button">Lanjutkan ke Penyedia</Button>
            <Button disabled={note.trim().length < 5} onClick={() => resolve("refund")} variant="outline" className="rounded-xl text-rose-700" data-testid="resolve-refund-button">Refund ke Pelanggan</Button>
          </div>
          <p className="text-xs text-stone-500">Refund bersifat SIMULASI — tidak ada uang yang benar-benar dikembalikan. Transfer manual dilakukan di luar aplikasi.</p>
        </div>
      )}
      {o.resolution && <p className="card-soft mb-6 p-4 text-sm" data-testid="resolution-note">Resolusi: <b>{o.resolution.action === "refund" ? "Refund ke pelanggan" : "Dilanjutkan ke penyedia"}</b> — {o.resolution.note}</p>}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card-soft p-5">
          <p className="mb-4 font-bold text-stone-900">Riwayat status</p>
          <ol className="space-y-3" data-testid="admin-order-timeline">
            {o.timeline.map((e, i) => (
              <li key={i} className="text-sm"><StatusBadge status={e.status} className="py-0.5" /> <span className="ml-2 text-stone-700">{e.note}</span>
                <p className="text-xs text-stone-400">{formatDate(e.at)} {formatTime(e.at)}</p></li>
            ))}
          </ol>
          {t && <p className="mt-4 rounded-xl bg-stone-50 p-3 text-xs text-stone-600" data-testid="admin-order-ledger">Ledger: bruto {rupiah(t.gross)} · komisi {t.commission_percent}% {rupiah(t.commission)} · bersih {rupiah(t.provider_net)} · status <b>{t.payout_status}</b></p>}
        </div>
        <div className="card-soft p-5">
          <p className="mb-4 font-bold text-stone-900">Riwayat chat (baca saja)</p>
          <div className="max-h-96 space-y-2 overflow-y-auto" data-testid="admin-order-chat">
            {o.messages.length === 0 && <p className="text-sm text-stone-500">Belum ada pesan.</p>}
            {o.messages.map((m) => (
              <div key={m.message_id} className="rounded-xl bg-stone-50 px-3 py-2 text-sm">
                <p className="text-xs font-semibold text-stone-500">{m.sender_id === o.customer_id ? o.customer_name : o.provider_name} · {formatTime(m.created_at)}</p>
                <p className="text-stone-800">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
